import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Upload,
    Printer,
    Trash2,
    Plus,
    X,
    Paperclip,
    Loader2,
    Share2,
    Eye,
    EyeOff,
    RotateCcw,
    Mail,
    History,
} from "lucide-react";
import {
    listContacts,
    listQuoteTemplates,
    createQuoteTemplate,
    updateQuoteTemplate,
    deleteQuoteTemplate,
    uploadQuoteTemplateLogo,
    uploadQuoteAttachment,
    fetchQuoteAttachmentData,
    createQuoteShare,
    listQuoteShares,
    revokeQuoteShare,
} from "../lib/api";
import { ShareLinksModal } from "./quote/ShareLinksModal";
import { SendQuoteEmailDialog } from "./quote/SendQuoteEmailDialog";
import { PlainInput, SaveStatusPill, MetaRow } from "./quote/QuoteFormFields";
import { buildAndPrintQuote } from "./quote/quotePrint";
import { TemplateBar } from "./quote/TemplateBar";
import { QuoteAttachmentsPanel } from "./quote/QuoteAttachmentsPanel";
import QuoteTemplateHistoryDialog from "./QuoteTemplateHistoryDialog";

const fmtMoney = (n) =>
    `$${(Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const todayFmt = () => {
    const d = new Date();
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

/**
 * Customer-facing Quote view for an Estimate.
 *
 * Line descriptions and amounts are editable inline by anyone in the
 * workgroup (locked estimates excepted). Edits flow back through
 * `onSave` so Priced-Lines and Full-Report stay in sync. The profit
 * margin is *distributed proportionally across every line amount*
 * the customer sees, so totals add up cleanly without ever surfacing
 * the word "Profit" on the printed quote.
 *
 * Everything above the line-items grid is editable and can be saved
 * as a Quote Template (logo + company info + valid-until default +
 * tax label/rate). The last-updated template is loaded by default.
 */
export default function QuoteView({
    estimate,
    project,
    onSave,
    onLocalUpdate,
}) {
    const lines = estimate?.lines || [];
    const locked = !!estimate?.locked;
    const canEditLines = !locked && typeof onSave === "function";

    // iter-84 — autosave status pill. Wraps every onSave call so we
    // can render "Saving…" / "Saved · 3 sec ago" / "Failed — retry"
    // next to the toolbar buttons. Failures keep the last failed
    // patch around so the user can re-trigger via the pill.
    const [saveStatus, setSaveStatus] = useState({
        state: "idle", // "idle" | "saving" | "saved" | "failed"
        at: 0,
        retryPatch: null,
        error: "",
    });
    // Tick once a second while the pill is showing "Saved" so the
    // relative timestamp keeps updating.
    const [, setNowTick] = useState(0);
    useEffect(() => {
        if (saveStatus.state !== "saved") return;
        const id = setInterval(() => setNowTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [saveStatus.state]);

    const onSaveTracked = useMemo(() => {
        if (typeof onSave !== "function") return undefined;
        const fn = async (patch) => {
            setSaveStatus((s) => ({ ...s, state: "saving", error: "" }));
            try {
                const r = await onSave(patch);
                setSaveStatus({
                    state: "saved",
                    at: Date.now(),
                    retryPatch: null,
                    error: "",
                });
                return r;
            } catch (err) {
                setSaveStatus({
                    state: "failed",
                    at: Date.now(),
                    retryPatch: patch,
                    error: err?.response?.data?.detail || err?.message || "Save failed",
                });
                throw err;
            }
        };
        return fn;
    }, [onSave]);

    const retrySave = async () => {
        if (!onSaveTracked || !saveStatus.retryPatch) return;
        try {
            await onSaveTracked(saveStatus.retryPatch);
        } catch {
            /* status already flipped to failed */
        }
    };
    const subtotal = useMemo(
        () =>
            lines
                .filter((ln) => !ln.hidden)
                .reduce((a, ln) => a + (Number(ln.line_total) || 0), 0),
        [lines],
    );

    // Template state
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [templatesLoaded, setTemplatesLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [templateName, setTemplateName] = useState("Default");
    const [showTplPicker, setShowTplPicker] = useState(false);
    const fileInputRef = useRef(null);

    // iter-71: live profit-margin slider — bakes profit into the
    // quote's totals in real time. The percentage is stored on the
    // estimate (`profit_margin_pct`) so the contractor's setting
    // sticks across reloads and the Cost-vs-Profit popup on the other
    // tabs stays in sync.
    const [profitPct, setProfitPct] = useState(
        Number(estimate?.profit_margin_pct) || 0,
    );
    useEffect(() => {
        setProfitPct(Number(estimate?.profit_margin_pct) || 0);
    }, [estimate?.id, estimate?.profit_margin_pct]);
    // Itemize toggle — when OFF (default) the customer-facing quote
    // hides per-line amounts and only shows subtotal/tax/total. When ON
    // the line amounts are printed. Persisted on the estimate so each
    // quote remembers the contractor's preference across reloads.
    const [itemized, setItemized] = useState(
        Boolean(estimate?.quote_itemized),
    );
    useEffect(() => {
        setItemized(Boolean(estimate?.quote_itemized));
    }, [estimate?.id, estimate?.quote_itemized]);
    const profitSaveTimer = useRef(null);
    const queueProfitSave = (pct) => {
        if (!canEditLines) return;
        if (profitSaveTimer.current) clearTimeout(profitSaveTimer.current);
        profitSaveTimer.current = setTimeout(() => {
            try {
                onSaveTracked?.({ profit_margin_pct: pct });
            } catch {
                /* toast handled upstream */
            }
        }, 500);
    };

    // iter-110: manual subtotal override. When the contractor types a
    // number into the Subtotal field, we treat it as the final subtotal
    // and scale every line proportionally so the printed itemised quote
    // still adds up. Tax + grand total recompute from the override.
    // ``null`` means "auto" (use profit-slider-driven totals).
    const [subtotalOverride, setSubtotalOverride] = useState(
        estimate?.subtotal_override && Number(estimate.subtotal_override) > 0
            ? Number(estimate.subtotal_override)
            : null,
    );
    useEffect(() => {
        setSubtotalOverride(
            estimate?.subtotal_override && Number(estimate.subtotal_override) > 0
                ? Number(estimate.subtotal_override)
                : null,
        );
    }, [estimate?.id, estimate?.subtotal_override]);
    const [subtotalDraft, setSubtotalDraft] = useState("");
    const subtotalSaveTimer = useRef(null);
    // Backend uses 0 as the "clear override" sentinel because pydantic
    // v2's exclude_none=True on the PATCH route strips explicit nulls.
    const queueSubtotalOverrideSave = (value) => {
        if (!canEditLines) return;
        if (subtotalSaveTimer.current) clearTimeout(subtotalSaveTimer.current);
        subtotalSaveTimer.current = setTimeout(() => {
            try {
                onSaveTracked?.({ subtotal_override: Number(value) || 0 });
            } catch {
                /* toast handled upstream */
            }
        }, 500);
    };

    // Header fields (editable, template-backed)
    const [logoDataUrl, setLogoDataUrl] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [addr1, setAddr1] = useState("");
    const [addr2, setAddr2] = useState("");
    const [addr3, setAddr3] = useState("");
    const [companyPhone, setCompanyPhone] = useState("");
    const [companyEmail, setCompanyEmail] = useState("");
    const [preparedBy, setPreparedBy] = useState("");
    const [validDays, setValidDays] = useState("30 Days");
    const [taxLabel, setTaxLabel] = useState("13% Ontario Harmonized Sales Tax");
    const [taxRate, setTaxRate] = useState(0.13);

    // Customer state
    const [contacts, setContacts] = useState([]);
    const [contactId, setContactId] = useState("");
    const [custName, setCustName] = useState("");
    const [custAddr, setCustAddr] = useState("");
    const [custCity, setCustCity] = useState("");
    const [custPhone, setCustPhone] = useState("");
    const [custEmail, setCustEmail] = useState("");
    // iter-179: track template save state so the header UI can tell
    // the user "changes saved" vs "saving…".
    const templateSaveTimer = useRef(null);
    // iter-180 — history dialog.
    const [historyOpen, setHistoryOpen] = useState(false);

    // Attachments — image / PDF pages appended to the printed quote.
    // Source of truth is `estimate.quote_attachments`; uploads commit
    // back through onSave so workgroup members see each other's edits.
    // The "save to template" toggle copies the current attachments
    // into the next template save so disclaimers / T&Cs persist.
    const fileAttachInputRef = useRef(null);
    const [uploadingAttach, setUploadingAttach] = useState(false);
    const [attachToTemplate, setAttachToTemplate] = useState(true);
    const attachments = Array.isArray(estimate?.quote_attachments)
        ? estimate.quote_attachments
        : [];

    const date = todayFmt();
    const quoteId = project?.job_id || project?.id?.slice(0, 8) || "";

    // Load templates + contacts on mount. Auto-apply most-recent template.
    useEffect(() => {
        (async () => {
            try {
                const [tpls, ctcs] = await Promise.all([
                    listQuoteTemplates(),
                    listContacts(),
                ]);
                setTemplates(tpls || []);
                setContacts(ctcs || []);
                if ((tpls || []).length > 0) {
                    applyTemplate(tpls[0]);
                    setTemplateId(tpls[0].id);
                }
            } catch {
                // Non-fatal — user just gets blank template + blank customer dropdown
            } finally {
                setTemplatesLoaded(true);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyTemplate = (t) => {
        setTemplateName(t.name || "Template");
        setLogoDataUrl(t.logo_data_url || "");
        setCompanyName(t.company_name || "");
        const al = t.company_address_lines || ["", "", ""];
        setAddr1(al[0] || "");
        setAddr2(al[1] || "");
        setAddr3(al[2] || "");
        setCompanyPhone(t.company_phone || "");
        setCompanyEmail(t.company_email || "");
        setPreparedBy(t.prepared_by || "");
        setValidDays(t.valid_days || "30 Days");
        setTaxLabel(t.tax_label || "13% Ontario Harmonized Sales Tax");
        setTaxRate(typeof t.tax_rate === "number" ? t.tax_rate : 0.13);
        // iter-145 — DO NOT auto-copy the template's attachments onto
        // the estimate. Previously we blanket-applied them whenever the
        // estimate had no attachments, which meant deleting the "extra
        // pages" ghost from an estimate silently brought them back the
        // next time the template touched it. Users now attach pages
        // explicitly per estimate via the Extra Pages panel; the "Save
        // with template" toggle in that panel is the sole opt-in path
        // for a page to persist across future quotes.
    };

    const selectContact = (id) => {
        setContactId(id);
        const c = contacts.find((x) => x.id === id);
        if (!c) {
            setCustName("");
            setCustAddr("");
            setCustCity("");
            setCustPhone("");
            setCustEmail("");
            return;
        }
        // iter-179 — customer *identity* (name / phone / email) comes
        // from the linked contact, but the *site address* comes from
        // the project itself. The project is what the quote is for,
        // and its address is what the crew will be working at — which
        // may not match the contact's mailing/billing address (a
        // property-management contact, an out-of-town owner, etc.).
        setCustName(c.name || "");
        setCustPhone(c.cell_phone || c.work_phone || c.phone || "");
        setCustEmail(c.email || "");
        const projStreet = project?.street_address || project?.location || c.address || "";
        setCustAddr(projStreet);
        const cityLine = [
            project?.city,
            project?.province,
            project?.postal_code,
        ]
            .filter(Boolean)
            .join(", ");
        setCustCity(
            cityLine
                || [c.city, c.country].filter(Boolean).join(", ")
                || "",
        );
    };

    // iter-179 — auto-select the project's linked contact once contacts
    // load. If the project has `contact_id` set, pre-populate the
    // customer block so the contractor doesn't have to hunt in the
    // dropdown. Only fires when contactId is still empty so a manual
    // override sticks.
    useEffect(() => {
        if (!templatesLoaded) return;
        if (contactId) return;
        const linkedId = project?.contact_id;
        if (!linkedId) return;
        if (!contacts.some((c) => c.id === linkedId)) return;
        selectContact(linkedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templatesLoaded, project?.contact_id, contacts]);

    const handleLogoUpload = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        if (file.size > 512 * 1024) {
            toast.error("Logo must be under 512 KB");
            return;
        }
        setUploadingLogo(true);
        try {
            const { data_url } = await uploadQuoteTemplateLogo(file);
            setLogoDataUrl(data_url);
            // iter-179 — persist immediately so the logo isn't lost if
            // the user closes the dialog without touching "Save as
            // template". Creates a template on the fly if the user
            // doesn't have one yet.
            try {
                if (templateId) {
                    const updated = await updateQuoteTemplate(templateId, {
                        logo_data_url: data_url,
                    });
                    setTemplates((prev) => [
                        updated,
                        ...prev.filter((p) => p.id !== templateId),
                    ]);
                } else {
                    const created = await createQuoteTemplate({
                        ...buildPayload(),
                        logo_data_url: data_url,
                    });
                    setTemplates((prev) => [created, ...prev]);
                    setTemplateId(created.id);
                    setTemplateName(created.name);
                }
                toast.success("Logo saved");
            } catch (persistErr) {
                toast.error(
                    persistErr?.response?.data?.detail
                        || "Logo uploaded but couldn't be saved to template. Click Save as template.",
                );
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Logo upload failed");
        } finally {
            setUploadingLogo(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const buildPayload = () => ({
        name: templateName.trim() || "Template",
        logo_data_url: logoDataUrl,
        company_name: companyName,
        company_address_lines: [addr1, addr2, addr3],
        company_phone: companyPhone,
        company_email: companyEmail,
        prepared_by: preparedBy,
        valid_days: validDays,
        tax_label: taxLabel,
        tax_rate: Number(taxRate) || 0,
        // When the user wants to bake disclaimers / T&Cs into the
        // template itself, ship the current attachment metadata —
        // bytes already live in object storage so this is cheap.
        attachments: attachToTemplate ? attachments : [],
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            if (templateId) {
                const updated = await updateQuoteTemplate(
                    templateId,
                    buildPayload(),
                );
                setTemplates((prev) =>
                    [updated, ...prev.filter((p) => p.id !== templateId)],
                );
                toast.success("Template updated");
            } else {
                const created = await createQuoteTemplate(buildPayload());
                setTemplates((prev) => [created, ...prev]);
                setTemplateId(created.id);
                toast.success("Template saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save template");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAsNew = async () => {
        const name = window.prompt(
            "Name for new template:",
            `${templateName} (copy)`,
        );
        if (!name) return;
        setSaving(true);
        try {
            const created = await createQuoteTemplate({
                ...buildPayload(),
                name,
            });
            setTemplates((prev) => [created, ...prev]);
            setTemplateId(created.id);
            setTemplateName(created.name);
            toast.success("Saved as new template");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    // iter-179 — debounced auto-save for the header fields. The
    // "Save as template" button stays for explicit saves + "Save as
    // new", but this effect keeps a template's copy up-to-date any
    // time the user tweaks the company name, address, phone, email,
    // etc. Fires 900 ms after the last change, only when we have a
    // template selected — we don't want to spam-create templates.
    useEffect(() => {
        if (!templatesLoaded) return;
        if (!templateId) return;
        if (templateSaveTimer.current) clearTimeout(templateSaveTimer.current);
        templateSaveTimer.current = setTimeout(async () => {
            try {
                const updated = await updateQuoteTemplate(templateId, {
                    company_name: companyName,
                    company_address_lines: [addr1, addr2, addr3],
                    company_phone: companyPhone,
                    company_email: companyEmail,
                    prepared_by: preparedBy,
                });
                setTemplates((prev) => [
                    updated,
                    ...prev.filter((p) => p.id !== templateId),
                ]);
            } catch {
                /* silent — user still has manual Save button */
            }
        }, 900);
        return () => {
            if (templateSaveTimer.current) clearTimeout(templateSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        templatesLoaded,
        templateId,
        companyName,
        addr1,
        addr2,
        addr3,
        companyPhone,
        companyEmail,
        preparedBy,
    ]);

    const handleDeleteTemplate = async () => {
        if (!templateId) return;
        if (!window.confirm(`Delete template "${templateName}"?`)) return;
        try {
            await deleteQuoteTemplate(templateId);
            const next = templates.filter((t) => t.id !== templateId);
            setTemplates(next);
            if (next.length > 0) {
                applyTemplate(next[0]);
                setTemplateId(next[0].id);
            } else {
                setTemplateId("");
                setTemplateName("Default");
            }
            toast.success("Template deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete");
        }
    };


    // Effective profit multiplier: the manual subtotal override
    // (if set) wins over the profit slider so the number the
    // contractor typed always shows through unchanged.
    const profitMultiplier =
        subtotalOverride != null && subtotal > 0
            ? subtotalOverride / subtotal
            : 1 + (Number(profitPct) || 0) / 100;
    const baseWithProfit = subtotal * profitMultiplier;
    const profit = baseWithProfit - subtotal;
    const tax = baseWithProfit * (Number(taxRate) || 0);
    const grand = baseWithProfit + tax;

    // Profit is distributed proportionally across every line so the
    // amounts the customer sees actually add up to the subtotal —
    // never as a separate "Profit" row.
    const displayedAmount = (ln) =>
        (Number(ln?.line_total) || 0) * profitMultiplier;

    // Print / Export PDF — delegates to the extracted module so the
    // printable HTML and pdfjs flattening logic live separately from
    // the React render code.
    const handlePrint = () =>
        buildAndPrintQuote({
            companyName, addr1, addr2, addr3, companyPhone, companyEmail,
            preparedBy, logoDataUrl,
            custName, custAddr, custCity, custPhone, custEmail,
            date, validDays, quoteId, taxLabel,
            lines, attachments, itemized, profitMultiplier,
            baseWithProfit, tax, grand,
            taxRate: Number(taxRate) || 0,
        });

    // -------- Inline line editing (description + amount) --------
    // Local edits (description text, amount string) are buffered so
    // typing stays smooth; we persist on blur / Enter.
    const [editBuf, setEditBuf] = useState({}); // { [idx]: { name?, amountStr? } }
    // Whether to render hidden (soft-deleted) lines in the on-screen
    // quote table. Defaults to false — they stay tucked behind a
    // single "Hidden lines (N) — show" pill so the working table
    // stays clean. The pill always renders when any line is hidden.
    const [showHidden, setShowHidden] = useState(false);
    const setBuf = (idx, patch) =>
        setEditBuf((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), ...patch } }));
    const clearBuf = (idx) =>
        setEditBuf((prev) => {
            const next = { ...prev };
            delete next[idx];
            return next;
        });

    // Soft-hide a quote line (or restore it). Hidden lines stay in the
    // estimate but are excluded from the printable quote, totals, and
    // the public e-sign share. They render greyed/struck-through on
    // screen with a Restore action so the contractor can bring them
    // back at any time.
    const toggleLineHidden = (idx) => {
        const ln = lines[idx];
        if (!ln) return;
        const willHide = !ln.hidden;
        const newLines = lines.map((l, i) =>
            i === idx ? { ...l, hidden: willHide } : l,
        );
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        toast.success(
            willHide ? "Line hidden from quote" : "Line restored to quote",
        );
    };

    const restoreAllHidden = () => {
        if (!lines.some((l) => l.hidden)) return;
        const newLines = lines.map((l) => ({ ...l, hidden: false }));
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        toast.success("All hidden lines restored");
    };

    // iter-98: add a blank line to the quote. Matches the Add Line UX on
    // the Full Report tab — defaults sane so the user can type a label
    // and amount immediately.
    const addLine = () => {
        const blank = {
            item_id: "",
            category: "",
            name: "",
            description: "",
            quantity: 1,
            unit: "each",
            unit_price: 0,
            price_type: "fixed",
            markup_type: "percent",
            markup_value: 0,
            subtotal: 0,
            markup_amount: 0,
            line_total: 0,
            notes: "",
        };
        const newLines = [...lines, blank];
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        // Auto-focus the newly-added row's name input so the user can
        // start typing immediately without hunting for the cursor.
        setTimeout(() => {
            const el = document.querySelector(
                `[data-testid="quote-line-${newLines.length - 1}-desc"]`,
            );
            if (el) {
                el.focus();
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 60);
    };

    const commitDescription = (idx) => {        const buf = editBuf[idx];
        if (!buf || typeof buf.name !== "string") return;
        const ln = lines[idx];
        if (!ln) return;
        const trimmed = buf.name;
        if ((ln.name || "") === trimmed) {
            clearBuf(idx);
            return;
        }
        const newLines = lines.map((l, i) =>
            i === idx ? { ...l, name: trimmed } : l,
        );
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        clearBuf(idx);
    };

    // iter-110: notes editing — a small second-line under each line's
    // name so contractors can add extra detail (spec, exclusions, colour
    // choice) without inflating the main label. Buffered like name /
    // amount so typing stays smooth; persisted on blur.
    const commitNotes = (idx) => {
        const buf = editBuf[idx];
        if (!buf || typeof buf.notesStr !== "string") return;
        const ln = lines[idx];
        if (!ln) return;
        const trimmed = buf.notesStr;
        if ((ln.notes || "") === trimmed) {
            clearBuf(idx);
            return;
        }
        const newLines = lines.map((l, i) =>
            i === idx ? { ...l, notes: trimmed } : l,
        );
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        clearBuf(idx);
    };

    // -------- Attachments (extra pages on the printed quote) --------
    const persistAttachments = (next) => {
        onLocalUpdate?.({ quote_attachments: next });
        try {
            onSaveTracked?.({ quote_attachments: next });
        } catch {
            /* upstream toast */
        }
    };

    const handleAttachmentUpload = async (ev) => {
        const file = ev.target.files?.[0];
        if (fileAttachInputRef.current) fileAttachInputRef.current.value = "";
        if (!file) return;
        if (!canEditLines) {
            toast.error("Bid is locked — unlock to add attachments.");
            return;
        }
        if (attachments.length >= 8) {
            toast.error("Maximum 8 attachments per quote.");
            return;
        }
        const isImage = (file.type || "").startsWith("image/");
        const isPdf = (file.type || "") === "application/pdf"
            || (file.name || "").toLowerCase().endsWith(".pdf");
        if (!isImage && !isPdf) {
            toast.error("Only images and PDFs can be appended.");
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            toast.error("File too large (max 15 MB).");
            return;
        }
        setUploadingAttach(true);
        try {
            const meta = await uploadQuoteAttachment(file);
            const next = [...attachments, meta];
            persistAttachments(next);
            toast.success(`Attached "${meta.name}"`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingAttach(false);
        }
    };

    const removeAttachment = (id) => {
        if (!canEditLines) return;
        persistAttachments(attachments.filter((a) => a.id !== id));
    };

    const moveAttachment = (id, dir) => {
        if (!canEditLines) return;
        const idx = attachments.findIndex((a) => a.id === id);
        if (idx < 0) return;
        const target = idx + dir;
        if (target < 0 || target >= attachments.length) return;
        const next = attachments.slice();
        const [item] = next.splice(idx, 1);
        next.splice(target, 0, item);
        persistAttachments(next);
    };

    // -------- Share / e-sign flow --------
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shares, setShares] = useState([]);
    const [creatingShare, setCreatingShare] = useState(false);

    useEffect(() => {
        if (!estimate?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const rows = await listQuoteShares(estimate.id);
                if (!cancelled) setShares(rows || []);
            } catch {
                /* ignore — non-fatal */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [estimate?.id]);

    const buildShareSnapshot = () => ({
        quote_id: quoteId || "",
        date_str: date,
        company: {
            name: companyName,
            address_lines: [addr1, addr2, addr3].filter(Boolean),
            phone: companyPhone,
            email: companyEmail,
            prepared_by: preparedBy,
            logo_data_url: logoDataUrl,
            valid_days: validDays,
        },
        customer: {
            name: custName,
            address: custAddr,
            city: custCity,
            phone: custPhone,
            email: custEmail,
        },
        lines: lines
            .filter((ln) => !ln.hidden)
            .map((ln) => ({
                name: ln.name || ln.description || "",
                notes: ln.notes || "",
                amount: (Number(ln.line_total) || 0) * profitMultiplier,
            })),
        subtotal: baseWithProfit,
        tax_label: taxLabel,
        tax_rate: Number(taxRate) || 0,
        tax_amount: tax,
        grand_total: grand,
        attachments: attachments.map((a) => ({
            id: a.id,
            name: a.name,
            mime: a.mime,
            size: a.size,
            kind: a.kind,
            storage_path: a.storage_path,
        })),
    });

    const handleCreateShare = async () => {
        if (!estimate?.id) return;
        setCreatingShare(true);
        try {
            const created = await createQuoteShare(
                estimate.id,
                buildShareSnapshot(),
            );
            setShares((prev) => [created, ...prev]);
            setShareModalOpen(true);
            toast.success("Share link created");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create share");
        } finally {
            setCreatingShare(false);
        }
    };

    const handleRevokeShare = async (shareId) => {
        if (!window.confirm("Revoke this link? The customer won't be able to open it anymore.")) return;
        try {
            await revokeQuoteShare(shareId);
            setShares((prev) => prev.filter((s) => s.id !== shareId));
            toast.success("Share revoked");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't revoke");
        }
    };

    // -------- Send via Email (IMAP/SMTP account) --------
    const [sendEmailOpen, setSendEmailOpen] = useState(false);

    // Build the normalised quote_data payload the backend PDF renderer
    // expects. Mirrors what QuoteView shows on screen so the customer
    // PDF == the on-screen preview.
    const buildQuotePdfPayload = () => ({
        company: {
            name: companyName || "",
            address: [addr1, addr2, addr3].filter(Boolean).join("\n"),
            phone: companyPhone || "",
            email: companyEmail || "",
            logo_data_url: logoDataUrl || "",
        },
        customer: {
            name: custName || "",
            address: [custAddr, custCity].filter(Boolean).join("\n"),
            phone: custPhone || "",
            email: custEmail || "",
            job_label: estimate?.label || "",
        },
        lines: lines
            .filter((ln) => !ln.hidden)
            .map((ln) => ({
                description: ln.name || ln.description || "",
                amount: (Number(ln.line_total) || 0) * profitMultiplier,
            })),
        profit_pct: 0,                // already baked into amount above
        lines_have_profit: true,
        valid_days: parseInt(String(validDays).replace(/\D/g, ""), 10) || 30,
        footer_note: "",
        quote_id: (estimate?.id || "").slice(0, 8).toUpperCase(),
        date,
    });

    const commitAmount = (idx) => {
        const buf = editBuf[idx];
        if (!buf || typeof buf.amountStr !== "string") return;
        const ln = lines[idx];
        if (!ln) return;
        const displayed = Number(buf.amountStr.replace(/[$,\s]/g, "")) || 0;
        // Convert customer-facing amount back to raw line_total so the
        // distributed profit isn't double-counted on save.
        const rawLineTotal = profitMultiplier > 0
            ? displayed / profitMultiplier
            : displayed;
        // Collapse the line to a fixed-price single-unit row — that's
        // the only shape the backend recompute will keep intact for
        // an arbitrary amount.
        const newLines = lines.map((l, i) =>
            i === idx
                ? {
                      ...l,
                      price_type: "fixed",
                      unit_price: rawLineTotal,
                      quantity: 1,
                      markup_type: "percent",
                      markup_value: 0,
                      subtotal: rawLineTotal,
                      markup_amount: 0,
                      line_total: rawLineTotal,
                  }
                : l,
        );
        onLocalUpdate?.({ lines: newLines });
        try {
            onSaveTracked?.({ lines: newLines });
        } catch {
            /* upstream toast */
        }
        clearBuf(idx);
    };

    return (
        <div data-testid="quote-view" className="space-y-3">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 p-2 bg-[#0a0a0a] border border-white/15 rounded-sm">
                <TemplateBar
                    templates={templates}
                    templateId={templateId}
                    templateName={templateName}
                    showPicker={showTplPicker}
                    templatesLoaded={templatesLoaded}
                    saving={saving}
                    onTogglePicker={() => setShowTplPicker((s) => !s)}
                    onPickTemplate={(t) => {
                        applyTemplate(t);
                        setTemplateId(t.id);
                        setShowTplPicker(false);
                    }}
                    onTemplateNameChange={setTemplateName}
                    onSave={handleSave}
                    onSaveAsNew={handleSaveAsNew}
                    onDelete={handleDeleteTemplate}
                />
                <div className="flex-1" />
                {/* iter-180 — Template version history. Shown only
                    when a template is selected so it always has
                    something to point at. */}
                {templateId && (
                    <button
                        type="button"
                        onClick={() => setHistoryOpen(true)}
                        data-testid="quote-history-btn"
                        title="View / restore an older version of this template"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#1a1a1a] border border-white/15 text-slate-200 hover:text-white hover:bg-white/10"
                    >
                        <History className="w-3 h-3" />
                        History
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleCreateShare}
                    disabled={creatingShare || !estimate?.id || locked}
                    data-testid="quote-share-btn"
                    title={locked ? "Unlock the bid before sharing" : "Generate a customer-facing link with e-signature"}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#1a1a1a] border border-white/15 text-slate-200 hover:text-white hover:bg-white/10 disabled:opacity-50"
                >
                    {creatingShare ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Share2 className="w-3 h-3" />
                    )}
                    Share / e-sign
                    {shares.length > 0 && (
                        <span
                            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#1a3fd1] text-[10px] font-bold text-white"
                            data-testid="quote-share-count"
                        >
                            {shares.length}
                        </span>
                    )}
                </button>
                <SaveStatusPill
                    status={saveStatus}
                    onRetry={retrySave}
                    visible={canEditLines}
                />
                <button
                    type="button"
                    onClick={() => setSendEmailOpen(true)}
                    disabled={!estimate?.id || locked}
                    data-testid="quote-send-email-btn"
                    title={locked ? "Unlock the bid before emailing" : "Email this quote with PDF attached"}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#1a1a1a] border border-white/15 text-slate-200 hover:text-white hover:bg-white/10 disabled:opacity-50"
                >
                    <Mail className="w-3 h-3" />
                    Send via Email
                </button>
                <button
                    type="button"
                    onClick={handlePrint}
                    data-testid="quote-print"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#00C853] hover:bg-[#00E676] text-black font-bold transition-colors"
                >
                    <Printer className="w-3 h-3" />
                    Print / Export PDF
                </button>
            </div>

            {/* Live profit-margin slider. Starts at 0%, bakes profit
                into totals in real time without touching the stored
                estimate. Not included in the admin toolbar because the
                value stays local to the Quote view. */}
            <div
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-[#0a0a0a] border border-white/15 rounded-sm"
                data-testid="quote-profit-slider-wrap"
            >
                <div className="flex items-baseline justify-between sm:flex-col sm:items-start sm:min-w-[110px] sm:justify-center">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Add profit
                    </span>
                    <span
                        className="text-xl font-bold text-[#00C853] tabular-nums"
                        data-testid="quote-profit-pct"
                    >
                        {(Number(profitPct) || 0).toFixed(1)}%
                    </span>
                </div>
                <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.5"
                    value={profitPct}
                    onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        setProfitPct(v);
                        // Editing the slider clears any manual subtotal
                        // override — otherwise the two controls would fight.
                        if (subtotalOverride != null) {
                            setSubtotalOverride(null);
                            queueSubtotalOverrideSave(0);
                        }
                        queueProfitSave(v);
                    }}
                    disabled={!canEditLines}
                    data-testid="quote-profit-slider"
                    className="w-full sm:flex-1 accent-[#00C853] disabled:opacity-50"
                    aria-label="Add profit percentage"
                />
                <div
                    className="flex items-baseline justify-between sm:flex-col sm:items-end sm:min-w-[120px] px-3 py-1.5 bg-[#00C853]/10 border border-[#00C853]/40 rounded-sm"
                    data-testid="quote-profit-amount-box"
                >
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#00C853]/80">
                        Profit $
                    </span>
                    <span
                        className="text-lg font-bold text-[#00C853] tabular-nums"
                        data-testid="quote-profit-amount"
                    >
                        {fmtMoney(profit)}
                    </span>
                </div>
            </div>

            {/* Itemize toggle — contractor-only control, hides from
                customer print. OFF (default): the printed quote shows
                only Subtotal / Tax / Total. ON: every line amount is
                visible too. Persisted on the estimate as
                `quote_itemized`. */}
            <div
                className="flex items-center justify-between gap-3 p-3 bg-[#0a0a0a] border border-white/15 rounded-sm"
                data-testid="quote-itemize-row"
            >
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        Itemize on quote
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                        {itemized
                            ? "Customer sees every line price."
                            : "Customer sees only Subtotal / Tax / Total."}
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={itemized}
                    aria-label="Itemize line prices on the printed quote"
                    disabled={!canEditLines}
                    data-testid="quote-itemize-toggle"
                    onClick={() => {
                        const next = !itemized;
                        setItemized(next);
                        // Persist immediately — no debounce because it's a
                        // single boolean and the user expects instant
                        // server-side stickiness.
                        if (canEditLines) onSaveTracked?.({ quote_itemized: next });
                    }}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        itemized ? "bg-[#00C853]" : "bg-[#1a1a1a]"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            itemized ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                </button>
            </div>

            {/* Printable quote panel (light theme on purpose — reads well on paper) */}
            <div
                className="pf-quote bg-white text-slate-900 rounded-sm p-5 sm:p-6 border border-white/15"
                data-testid="quote-printable"
            >
                {/* Condensed header: logo + company (left) · meta (top-right) */}
                <div className="hdr grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-4 mb-4 pb-3 border-b border-slate-200">
                    {/* Logo (capped to 140×90 so it never dominates the page) */}
                    <div className="logo">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            data-testid="quote-logo-input"
                        />
                        {logoDataUrl ? (
                            <div className="relative group w-[140px] h-[90px] flex items-center justify-start">
                                <img
                                    src={logoDataUrl}
                                    alt="Company logo"
                                    className="max-w-[140px] max-h-[90px] w-auto h-auto object-contain"
                                    data-testid="quote-logo-preview"
                                />
                                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/85 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        className="px-2 py-1 text-[11px] rounded-sm bg-slate-800 text-white hover:bg-slate-700"
                                    >
                                        Replace
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setLogoDataUrl("")}
                                        className="p-1 rounded-sm bg-red-600 text-white hover:bg-red-500"
                                        aria-label="Remove logo"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingLogo}
                                data-testid="quote-logo-upload-btn"
                                className="w-[140px] h-[90px] border-2 border-dashed border-slate-300 hover:border-[#1a3fd1]/60 hover:bg-slate-50 rounded-sm flex flex-col items-center justify-center gap-0.5 text-slate-500 hover:text-[#8fa4f0] transition-colors print:hidden"
                            >
                                <Upload className="w-4 h-4" />
                                <span className="text-[10px] font-semibold">
                                    {uploadingLogo ? "Uploading…" : "Upload logo"}
                                </span>
                                <span className="text-[9px] text-slate-400">
                                    ≤512 KB
                                </span>
                            </button>
                        )}
                    </div>

                    {/* Company block — tight leading, no dead space */}
                    <div className="company text-[13px] leading-tight space-y-0">
                        <PlainInput
                            value={companyName}
                            onChange={setCompanyName}
                            placeholder="Company name"
                            testid="quote-company-name"
                            bold
                            larger
                        />
                        <PlainInput
                            value={addr1}
                            onChange={setAddr1}
                            placeholder="Street address"
                            testid="quote-company-addr1"
                        />
                        <PlainInput
                            value={addr2}
                            onChange={setAddr2}
                            placeholder="City, Province, Postal"
                            testid="quote-company-addr2"
                        />
                        <PlainInput
                            value={companyPhone}
                            onChange={setCompanyPhone}
                            placeholder="Phone: (xxx) xxx-xxxx"
                            testid="quote-company-phone"
                        />
                        <PlainInput
                            value={preparedBy}
                            onChange={setPreparedBy}
                            placeholder="Prepared by: Your name"
                            testid="quote-prepared-by"
                        />
                        <PlainInput
                            value={companyEmail}
                            onChange={setCompanyEmail}
                            placeholder="you@company.com"
                            testid="quote-company-email"
                        />
                    </div>

                    {/* Meta pinned top-right — compact stack */}
                    <div className="meta text-[11px] space-y-1 min-w-[200px] md:justify-self-end">
                        <MetaRow label="Date">
                            <span
                                className="val inline-block border border-slate-900 px-2 py-0.5 min-w-[92px] text-center bg-slate-50 text-[11px]"
                                data-testid="quote-date"
                            >
                                {date}
                            </span>
                        </MetaRow>
                        <MetaRow label="Valid Until">
                            <input
                                type="text"
                                value={validDays}
                                onChange={(e) => setValidDays(e.target.value)}
                                data-testid="quote-valid-days"
                                className="val border border-slate-900 px-2 py-0.5 min-w-[92px] text-center bg-white outline-none focus:bg-yellow-50 text-[11px]"
                            />
                        </MetaRow>
                        <MetaRow label="Quote ID">
                            <span
                                className="val inline-block border border-slate-900 px-2 py-0.5 min-w-[92px] text-center bg-slate-50 font-mono text-[11px]"
                                data-testid="quote-id"
                            >
                                {quoteId || "—"}
                            </span>
                        </MetaRow>
                    </div>
                </div>

                {/* Customer block */}
                <div className="cust grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                    <div>
                        <div className="cust-header bg-[#1a3fd1] text-white font-bold px-3 py-1.5 uppercase tracking-wide text-xs mb-2">
                            Customer
                        </div>
                        <select
                            value={contactId}
                            onChange={(e) => selectContact(e.target.value)}
                            data-testid="quote-customer-select"
                            className="w-full mb-2 px-2 py-1.5 text-sm border border-slate-300 rounded-sm bg-white print:hidden"
                        >
                            <option value="">— Select from contacts —</option>
                            {contacts.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                    {c.company ? ` · ${c.company}` : ""}
                                </option>
                            ))}
                        </select>
                        <PlainInput
                            value={custName}
                            onChange={setCustName}
                            placeholder="Customer name"
                            testid="quote-customer-name"
                            bold
                            light
                        />
                        <PlainInput
                            value={custAddr}
                            onChange={setCustAddr}
                            placeholder="Customer address"
                            testid="quote-customer-addr"
                            light
                        />
                        <PlainInput
                            value={custCity}
                            onChange={setCustCity}
                            placeholder="City, Province / Postal"
                            testid="quote-customer-city"
                            light
                        />
                    </div>
                    <div className="flex flex-col justify-end">
                        <PlainInput
                            value={custPhone}
                            onChange={setCustPhone}
                            placeholder="Phone"
                            testid="quote-customer-phone"
                            light
                        />
                        <PlainInput
                            value={custEmail}
                            onChange={setCustEmail}
                            placeholder="Email"
                            testid="quote-customer-email"
                            light
                        />
                    </div>
                </div>

                {/* Line items */}
                {(() => {
                    const hiddenCount = lines.filter((l) => l.hidden).length;
                    if (hiddenCount === 0) return null;
                    return (
                        <div
                            className="print:hidden flex items-center justify-between gap-3 px-3 py-2 mb-1.5 bg-amber-50 border border-amber-200 rounded-sm text-xs"
                            data-testid="quote-hidden-banner"
                        >
                            <button
                                type="button"
                                onClick={() => setShowHidden((s) => !s)}
                                data-testid="quote-hidden-toggle"
                                className="flex items-center gap-1.5 font-medium text-amber-900 hover:text-amber-950"
                            >
                                {showHidden ? (
                                    <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                    <Eye className="w-3.5 h-3.5" />
                                )}
                                {hiddenCount} hidden line{hiddenCount === 1 ? "" : "s"}
                                <span className="text-amber-700">
                                    — {showHidden ? "hide" : "show"}
                                </span>
                            </button>
                            {canEditLines && (
                                <button
                                    type="button"
                                    onClick={restoreAllHidden}
                                    data-testid="quote-restore-all-hidden"
                                    className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-semibold"
                                >
                                    <RotateCcw className="w-3 h-3" />
                                    Restore all
                                </button>
                            )}
                        </div>
                    );
                })()}
                <table className="lines w-full text-sm border-collapse">
                    <thead>
                        <tr className="border-b-2 border-slate-900">
                            <th className="text-left px-3 py-2.5 w-[50px] text-xs uppercase tracking-wide">
                                Item
                            </th>
                            <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wide">
                                Description
                            </th>
                            {itemized && (
                                <th className="right text-right px-3 py-2.5 w-[140px] text-xs uppercase tracking-wide print:table-cell">
                                    Amount
                                </th>
                            )}
                            {!itemized && canEditLines && (
                                <th
                                    className="right text-right px-3 py-2.5 w-[140px] text-xs uppercase tracking-wide print:hidden"
                                    title="Line prices are hidden on the printed customer quote — toggle 'Itemize' above to show them"
                                >
                                    Amount
                                    <span className="ml-1 text-[9px] normal-case tracking-normal text-slate-400 font-normal">
                                        (hidden from customer)
                                    </span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody data-testid="quote-lines">
                        {lines.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={itemized || canEditLines ? 3 : 2}
                                    className="text-center text-slate-500 py-6"
                                >
                                    No priced lines from the estimate yet.
                                </td>
                            </tr>
                        ) : (
                            lines.map((ln, i) => {
                                if (ln.hidden && !showHidden) return null;
                                const buf = editBuf[i] || {};
                                const descValue =
                                    typeof buf.name === "string"
                                        ? buf.name
                                        : ln.name || ln.description || "";
                                const amtValue =
                                    typeof buf.amountStr === "string"
                                        ? buf.amountStr
                                        : displayedAmount(ln).toFixed(2);
                                return (
                                    <tr
                                        key={i}
                                        className={`border-b border-slate-200 ${ln.hidden ? "opacity-40 line-through" : ""}`}
                                        data-testid={`quote-line-${i}`}
                                    >
                                        <td className="num px-3 py-3 align-top">
                                            {i + 1}
                                        </td>
                                        <td className="px-3 py-3 align-top">
                                            <div className="flex items-start gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <input
                                                        type="text"
                                                        value={descValue}
                                                        onChange={(e) =>
                                                            setBuf(i, { name: e.target.value })
                                                        }
                                                        onBlur={() => commitDescription(i)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                e.currentTarget.blur();
                                                            }
                                                        }}
                                                        disabled={!canEditLines || ln.hidden}
                                                        data-testid={`quote-line-${i}-desc`}
                                                        placeholder="Line name"
                                                        className="w-full bg-transparent outline-none border-b border-transparent hover:border-slate-300 focus:border-[#1a3fd1]/50 py-0 leading-snug text-[13px] font-medium text-slate-900 placeholder:text-slate-400 disabled:opacity-70"
                                                    />
                                                    {(canEditLines && !ln.hidden) ? (
                                                        <input
                                                            type="text"
                                                            value={
                                                                typeof buf.notesStr === "string"
                                                                    ? buf.notesStr
                                                                    : ln.notes || ""
                                                            }
                                                            onChange={(e) =>
                                                                setBuf(i, { notesStr: e.target.value })
                                                            }
                                                            onBlur={() => commitNotes(i)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                            data-testid={`quote-line-${i}-notes`}
                                                            placeholder="Description / notes (optional)"
                                                            className="w-full bg-transparent outline-none border-b border-transparent hover:border-slate-300 focus:border-[#1a3fd1]/50 py-0 mt-1 leading-snug text-xs text-slate-500 placeholder:text-slate-400"
                                                        />
                                                    ) : (
                                                        ln.notes && (
                                                            <div className="text-xs text-slate-500 mt-0.5">
                                                                {ln.notes}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                                {canEditLines && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleLineHidden(i)}
                                                        data-testid={`quote-line-${i}-${ln.hidden ? "restore" : "hide"}`}
                                                        aria-label={
                                                            ln.hidden
                                                                ? "Restore line to quote"
                                                                : "Hide line from quote"
                                                        }
                                                        title={
                                                            ln.hidden
                                                                ? "Restore — include this line in the quote again"
                                                                : "Remove from quote — line stays saved and can be restored"
                                                        }
                                                        className={`print:hidden shrink-0 mt-0.5 p-1 rounded-sm transition-colors no-underline ${
                                                            ln.hidden
                                                                ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                                                                : "text-slate-400 hover:text-red-500 hover:bg-red-50"
                                                        }`}
                                                    >
                                                        {ln.hidden ? (
                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                        ) : (
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`amt px-3 py-3 text-right tabular-nums whitespace-nowrap font-semibold ${itemized ? "" : "print:hidden"}`}>
                                            <span className="text-slate-500 mr-0.5">$</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={amtValue}
                                                onChange={(e) =>
                                                    setBuf(i, { amountStr: e.target.value })
                                                }
                                                onFocus={(e) => e.currentTarget.select()}
                                                onBlur={() => commitAmount(i)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        e.currentTarget.blur();
                                                    }
                                                }}
                                                disabled={!canEditLines || ln.hidden}
                                                data-testid={`quote-line-${i}-amount`}
                                                className="w-[88px] bg-transparent outline-none border-b border-transparent hover:border-slate-300 focus:border-[#1a3fd1]/50 py-0 text-right tabular-nums font-semibold text-slate-900 disabled:opacity-70"
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {/* iter-98: Add Line button matching the Full Report
                    add-line UX. Hidden from print so customer-facing PDF
                    stays clean. */}
                {canEditLines && (
                    <button
                        type="button"
                        onClick={addLine}
                        data-testid="quote-add-line"
                        className="print:hidden mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-sm border-2 border-dashed border-[#1a3fd1]/40 hover:border-[#1a3fd1]/60 text-sm font-bold uppercase tracking-wider text-[#8fa4f0] hover:bg-[#1a3fd1]/5 transition-colors"
                    >
                        <Plus className="w-4 h-4" /> Add new line
                    </button>
                )}

                {/* Totals */}
                <div className="totals mt-2">
                    <div className="trow flex justify-between items-center py-2.5 px-3 border-b border-slate-200 gap-2">
                        <span className="lbl text-xs uppercase tracking-wide font-bold">
                            Subtotal
                        </span>
                        {/* iter-110: contractor can type a manual subtotal.
                            Tax + grand total recompute live; line amounts
                            scale proportionally so an itemised quote still
                            adds up. Print view collapses the editor into a
                            read-only value. */}
                        <div className="flex items-center gap-2">
                            {canEditLines ? (
                                <>
                                    <span className="text-slate-400 tabular-nums print:hidden">$</span>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={
                                            subtotalDraft !== ""
                                                ? subtotalDraft
                                                : baseWithProfit.toFixed(2)
                                        }
                                        onFocus={(e) => {
                                            setSubtotalDraft(baseWithProfit.toFixed(2));
                                            e.target.select();
                                        }}
                                        onChange={(e) => setSubtotalDraft(e.target.value)}
                                        onBlur={() => {
                                            const raw = subtotalDraft.replace(/[$,\s]/g, "");
                                            const val = Number(raw);
                                            setSubtotalDraft("");
                                            if (!Number.isFinite(val) || val <= 0) {
                                                setSubtotalOverride(null);
                                                queueSubtotalOverrideSave(0);
                                                return;
                                            }
                                            // If the typed number matches the
                                            // auto-computed value, clear the
                                            // override so the profit slider
                                            // regains control.
                                            const autoBase =
                                                subtotal * (1 + (Number(profitPct) || 0) / 100);
                                            if (Math.abs(val - autoBase) < 0.01) {
                                                setSubtotalOverride(null);
                                                queueSubtotalOverrideSave(0);
                                                return;
                                            }
                                            setSubtotalOverride(val);
                                            queueSubtotalOverrideSave(val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") e.currentTarget.blur();
                                        }}
                                        data-testid="quote-subtotal-input"
                                        className="w-28 text-right text-sm font-semibold tabular-nums bg-white border border-slate-300 rounded-sm px-2 py-0.5 focus:border-[#1a3fd1]/50 focus:ring-1 focus:ring-[#1a3fd1] outline-none print:hidden"
                                        title="Type a manual subtotal — tax and grand total will recompute"
                                    />
                                </>
                            ) : null}
                            <span
                                className={`tabular-nums font-semibold ${canEditLines ? "hidden print:inline" : ""}`}
                                data-testid="quote-subtotal"
                            >
                                {fmtMoney(baseWithProfit)}
                            </span>
                            {subtotalOverride != null && canEditLines && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSubtotalOverride(null);
                                        setSubtotalDraft("");
                                        queueSubtotalOverrideSave(0);
                                    }}
                                    data-testid="quote-subtotal-reset"
                                    title="Clear manual subtotal — revert to profit-slider value"
                                    className="print:hidden text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-[#8fa4f0] border border-slate-300 hover:border-[#1a3fd1]/60 rounded-sm px-1.5 py-0.5"
                                >
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="trow flex justify-between items-center py-2.5 px-3 border-b border-slate-200">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="lbl text-xs uppercase tracking-wide font-bold whitespace-nowrap">
                                Tax
                            </span>
                            <input
                                type="text"
                                value={taxLabel}
                                onChange={(e) => setTaxLabel(e.target.value)}
                                data-testid="quote-tax-label"
                                className="flex-1 min-w-0 text-xs text-slate-600 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-[#1a3fd1]/50 outline-none"
                            />
                            <input
                                type="number"
                                step="0.001"
                                min="0"
                                max="1"
                                value={taxRate}
                                onChange={(e) => setTaxRate(Number(e.target.value) || 0)}
                                data-testid="quote-tax-rate"
                                className="w-16 text-xs text-right bg-slate-50 border border-slate-300 rounded-sm px-1 py-0.5 print:hidden"
                                title="Tax rate as decimal (e.g. 0.13 for 13%)"
                            />
                        </div>
                        <span
                            className="tabular-nums font-semibold ml-3"
                            data-testid="quote-tax-amount"
                        >
                            {fmtMoney(tax)}
                        </span>
                    </div>
                    <div className="trow grand flex justify-between py-3 px-3 bg-slate-100 font-extrabold text-base">
                        <span className="uppercase tracking-wide">
                            Grand Total
                        </span>
                        <span
                            className="tabular-nums"
                            data-testid="quote-grand-total"
                        >
                            {fmtMoney(grand)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Attachments — extra pages appended to the printed quote.
                Panel is in-app only; the actual content is rendered into
                the Print/Export popup, one page per image / per PDF page. */}
            <QuoteAttachmentsPanel
                attachments={attachments}
                canEditLines={canEditLines}
                uploadingAttach={uploadingAttach}
                attachToTemplate={attachToTemplate}
                onToggleSaveWithTemplate={setAttachToTemplate}
                onUploadFile={handleAttachmentUpload}
                onMove={moveAttachment}
                onRemove={removeAttachment}
                inputRef={fileAttachInputRef}
            />

            {shareModalOpen && (
                <ShareLinksModal
                    shares={shares}
                    onClose={() => setShareModalOpen(false)}
                    onRevoke={handleRevokeShare}
                    onCreate={handleCreateShare}
                    creating={creatingShare}
                    defaultEmail={custEmail}
                    defaultName={custName}
                    onEmailed={(updated) =>
                        setShares((prev) =>
                            prev.map((s) => (s.id === updated.id ? updated : s)),
                        )
                    }
                />
            )}

            {sendEmailOpen && (
                <SendQuoteEmailDialog
                    estimateId={estimate?.id}
                    buildPayload={buildQuotePdfPayload}
                    defaultEmail={custEmail}
                    defaultName={custName}
                    companyName={companyName}
                    onClose={() => setSendEmailOpen(false)}
                />
            )}

            {/* iter-180 — Quote template version history */}
            <QuoteTemplateHistoryDialog
                open={historyOpen}
                onOpenChange={setHistoryOpen}
                templateId={templateId}
                templateName={templateName}
                onRestored={(restored) => {
                    applyTemplate(restored);
                    setTemplates((prev) => [
                        restored,
                        ...prev.filter((p) => p.id !== restored.id),
                    ]);
                }}
            />
        </div>
    );
}

